'use client';

import { useMergeRefs } from '@floating-ui/react';
import { type MouseEventHandler, forwardRef, useCallback, useRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { useInteractiveElementKeyDownCallback } from '../../hooks/use-interactive-element-key-down-callback';
import { toggleCardRootRecipe } from './styles/toggle-card-root.styles';
import type { ToggleCardRootPropsInterface } from './types/toggle-card-root.types';

/**
 * @since 10.0.0
 */
export const ToggleCardRoot = forwardRef<HTMLDivElement, ToggleCardRootPropsInterface>(
  (props: ToggleCardRootPropsInterface, forwardedRef) => {
    const {
      selected,
      defaultSelected,
      onSelectedChange,
      outlined = true,
      className,
      css: cssProp,
      disabled = false,
      elevated = true,
      tone = 'neutral',
      onClick,
      onKeyDown,
      ...rest
    } = props;

    const [selectedState, setSelectedState] = useControllableState({
      prop: selected,
      defaultProp: defaultSelected,
      onChange: onSelectedChange,
    });

    const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        if (!disabled) {
          onClick?.(e);
          setSelectedState((prev) => !prev);
        }
      },
      [setSelectedState, disabled, onClick],
    );

    const toggleCardRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([toggleCardRef, forwardedRef]);

    const onKeyDownCallback = useInteractiveElementKeyDownCallback({
      ref: toggleCardRef,
      onClick: handleClick,
      onKeyDown,
    });

    const styles = css(
      toggleCardRootRecipe.raw({ outlined, disabled, elevated, tone, selected: !!selectedState }),
      cssProp,
    );

    const a11yDisabledState = useDisabledState({ disabled, focusable: true });

    return (
      <div
        ref={refs}
        // eslint-disable-next-line jsx-a11y(prefer-tag-over-role)
        role="button"
        onClick={handleClick}
        onKeyDown={onKeyDownCallback}
        tabIndex={0}
        data-c11n-component="ToggleCard.Root"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        aria-pressed={!!selectedState}
        {...rest}
        {...a11yDisabledState}
      />
    );
  },
);

ToggleCardRoot.displayName = 'ToggleCard.Root';
